import type { MediaRow } from '@/routes/media/types';

/**
 * The best available thumbnail for a media row, or `null` if there is none.
 *
 * Every admin list that leads with a picture needs the same three-step answer,
 * and getting it wrong is invisible: the cell renders its "no image" state and
 * looks like a record with no image rather than like a bug.
 *
 * That is exactly what happened. Task #71 moved images off `cover_path`-style
 * columns onto the media library, and the API resources went from `cover_url`
 * to a `cover` object with a `urls` map — but `NewsPage`, `StaffPage` and
 * `UsefulLinksPage` kept reading `row.cover_url`, `row.photo_url` and
 * `row.logo_url`. Those fields stopped being sent, so `undefined` fell into the
 * empty branch on every row, and the news list showed a placeholder against 676
 * articles that had a cover. The hand-written row types still declared the dead
 * fields, which is why TypeScript said nothing.
 *
 * Order matters:
 *
 * 1. `thumb` — the WebP conversion, and the right size for a 40px cell.
 * 2. `thumb_fallback` — the JPEG twin, for a browser without WebP.
 * 3. `original` — the full-size upload. Wasteful in a list, and better than an
 *    empty frame: a conversion that has not been generated yet comes back as an
 *    empty string rather than as a missing key, so this is the case where the
 *    image exists and its derivatives do not.
 */
export function thumbUrl(media: MediaRow | null | undefined): string | null {
  if (!media) {
    return null;
  }

  const { thumb, thumb_fallback: fallback, original } = media.urls;

  return thumb || fallback || original || null;
}
